import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useBlocker } from 'react-router-dom';
import {
    Save, ArrowLeft, MoreVertical, CheckCircle, Clock,
    AlertCircle, Maximize2, Minimize2, PauseCircle, PlayCircle, LogOut, FileText, Eye, Download, Loader2
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { generateSignatureAuditTrail } from '../utils/signatureAudit';
import QuizViewer from '../components/Tasks/QuizViewer';
import TrainingViewer from '../components/Tasks/TrainingViewer';
import ChecklistViewer from '../components/Tasks/ChecklistViewer';
import CombinedTaskViewer from '../components/Tasks/CombinedTaskViewer';
import PausedOverlay from '../components/Tasks/PausedOverlay';
import SubmissionConfirmModal from '../components/Tasks/SubmissionConfirmModal';
import TaskSuccessModal from '../components/Tasks/TaskSuccessModal';
import TaskFailureModal from '../components/Tasks/TaskFailureModal';
import ProofFileUpload from '../components/Tasks/ProofFileUpload';
import SignatureSection from '../components/Tasks/SignatureSection';
import FileViewer from '../components/Tasks/FileViewer';

// Timer Component


const EmployeeTaskDetailPage = () => {
    const { taskId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const basePath = user?.is_admin ? '/admin' : '/employee';

    // Task State
    const [task, setTask] = useState(null);
    const [loading, setLoading] = useState(true);

    // Workspace State
    const [isPaused, setIsPaused] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showPauseConfirm, setShowPauseConfirm] = useState(false);

    // Proof & Signature State
    const [proofFiles, setProofFiles] = useState([]);
    const [proofUrl, setProofUrl] = useState('');
    const [signatureData, setSignatureData] = useState('');
    const [previewFile, setPreviewFile] = useState(null);

    // Completion Modal State
    const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [showFailureModal, setShowFailureModal] = useState(false);
    const [completionResult, setCompletionResult] = useState(null); // { score, passingScore, missedTopics }
    const [pendingSubmission, setPendingSubmission] = useState(null); // { data, score }
    const [nextTask, setNextTask] = useState(null);
    const [validationState, setValidationState] = useState({ isValid: true, summary: [] });
    const [lastSubmissionCancelled, setLastSubmissionCancelled] = useState(null);
    const [activeTime, setActiveTime] = useState(0);
    const [isExportingPDF, setIsExportingPDF] = useState(false);

    // Inactivity Detection
    const inactivityTimerRef = useRef(null);
    const INACTIVITY_LIMIT = 30 * 60 * 1000; // 30 minutes

    const fetchTask = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            // Fetch specific assignment details which includes task data and sections
            const response = await api.get(`/tasks/employee/assignment/${taskId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success && response.data.task) {
                setTask(response.data.task);
                // Sync local paused state
                setIsPaused(response.data.task.status === 'paused');
            } else {
                toast.error('Task not found');
                navigate(`${basePath}/tasks`);
            }
        } catch (error) {
            console.error('Error fetching task:', error);
            navigate(`${basePath}/tasks`);
        } finally {
            setLoading(false);
        }
    }, [taskId, navigate]);

    useEffect(() => {
        fetchTask();
    }, [fetchTask]);

    // Inactivity Handler
    const resetInactivityTimer = useCallback(() => {
        if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
        // Only track inactivity if in progress
        if (!isPaused && task?.status === 'in_progress') {
            inactivityTimerRef.current = setTimeout(handleAutoPause, INACTIVITY_LIMIT);
        }
    }, [isPaused, task]);

    useEffect(() => {
        window.addEventListener('mousemove', resetInactivityTimer);
        window.addEventListener('keydown', resetInactivityTimer);
        window.addEventListener('click', resetInactivityTimer);
        window.addEventListener('scroll', resetInactivityTimer);

        resetInactivityTimer();

        return () => {
            window.removeEventListener('mousemove', resetInactivityTimer);
            window.removeEventListener('keydown', resetInactivityTimer);
            window.removeEventListener('click', resetInactivityTimer);
            window.removeEventListener('scroll', resetInactivityTimer);
            if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
        };
    }, [resetInactivityTimer]);

    const handleAutoPause = async () => {
        await handlePause('auto');
        // Toast is handled in handlePause or after
    };

    const handlePause = async (type = 'manual') => {
        try {
            const token = localStorage.getItem('token');
            // Optimistic update
            setIsPaused(true);

            await api.put(
                `/tasks/employee/${task.id}/status`,
                { status: 'paused', pause_type: type },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (type === 'auto') {
                toast('Task auto-paused due to inactivity', { icon: '⏸️' });
            } else {
                toast.success('Task paused and progress saved');
            }

            // Return to dashboard
            navigate(`${basePath}/tasks`);

        } catch (error) {
            console.error('Error pausing task:', error);
            setIsPaused(false);
            toast.error('Failed to pause task');
        }
    };

    const handleResume = async () => {
        try {
            const token = localStorage.getItem('token');
            // Optimistic update
            setIsPaused(false);

            await api.put(
                `/tasks/employee/${task.id}/status`,
                { status: 'in_progress' },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            fetchTask();
            toast.success('Task resumed');
        } catch (error) {
            console.error('Error resuming task:', error);
            setIsPaused(true);
            toast.error('Failed to resume task');
        }
    };

    const handleStartTask = async () => {
        try {
            const token = localStorage.getItem('token');
            // Only update status if it's not already in progress
            if (task.status === 'pending' || task.status === 'not_started' || task.status === 'viewed' || task.status === 'overdue' || task.status === 'missed') {
                // Optimistic Update
                const now = new Date().toISOString();
                setTask(prev => ({ ...prev, status: 'in_progress', started_at: now }));

                await api.put(
                    `/tasks/employee/${task.id}/status`,
                    { status: 'in_progress', started_at: new Date() },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                toast.success('Task started!');
                fetchTask(); // Refresh to get official server timestamps
            }
        } catch (error) {
            console.error('Error starting task:', error);
            toast.error('Failed to start task');
            // Revert on error (fetchTask will fix it)
            fetchTask();
        }
    };

    const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);

    // Determine if we are in "Full Screen / Workspace" mode
    // Condition: Task is In Progress or Paused.
    const isWorkspaceMode = task?.status === 'in_progress' || task?.status === 'paused';

    // Browser unload warning
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (isWorkspaceMode && !isPaused && !isSubmitting) {
                e.preventDefault();
                e.returnValue = ''; // Trigger browser warning
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [isWorkspaceMode, isPaused, isSubmitting]);

    // Active Timer
    useEffect(() => {
        let interval;
        if (isWorkspaceMode && !isPaused && !loading) {
            interval = setInterval(() => {
                setActiveTime(prev => prev + 1000);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [isWorkspaceMode, isPaused, loading]);

    const isLeavingRef = useRef(false);

    // React Router Blocker for Back Navigation etc.
    // Blocks navigation if in workspace mode, not paused, and not submitting
    const blocker = useBlocker(
        ({ currentLocation, nextLocation }) =>
            !isLeavingRef.current && isWorkspaceMode && !isPaused && !isSubmitting && currentLocation.pathname !== nextLocation.pathname
    );

    // Sync blocker state with modal
    // Note: We use blocker.state to trigger modal.
    useEffect(() => {
        if (blocker.state === "blocked") {
            setShowLeaveConfirm(true);
        }
    }, [blocker.state]);

    const handleLeave = () => {
        // If we are here, it means the user clicked the manual "Leave" button.
        // We just try to navigate. The blocker will catch it.
        navigate(`${basePath}/tasks`);
    };

    // Modal confirm action
    const confirmLeave = async () => {
        try {
            // Call API to reset task status
            // We use the existing handleAbandon logic but integrate it here
            await handleAbandon();

            if (blocker.state === "blocked") {
                blocker.proceed();
            } else {
                // Fallback if somehow blocker didn't catch it or manual logic
                isLeavingRef.current = true; // Bypass blocker
                setShowLeaveConfirm(false);
                navigate(`${basePath}/tasks`);
            }
        } catch (error) {
            console.error("Failed to abandon task:", error);
            // Even if API fails, we probably still want to let them leave? 
            // Or show error? User wants "reset", so if reset fails, maybe warn?
            // tailored to user request: "if user leave... status will be not started"
            // So we MUST ensure API call succeeds for feature to work.
            toast.error("Failed to reset task status. Please try again.");
            // We do NOT navigate if failed, to avoid inconsistency.
        }
    };

    // Modal cancel action
    const cancelLeave = () => {
        if (blocker.state === "blocked") {
            blocker.reset();
        }
        setShowLeaveConfirm(false);
    };

    const handleAbandon = async () => {
        try {
            const token = localStorage.getItem('token');
            // Check if we have assignmentId (from URL? No, URL has taskId usually?)
            // Wait, route is /employee/tasks/:taskId
            // Frontend Component uses `task` object which usually has `id` (task id) AND `assignment_id`?
            // Or does the route /tasks/:taskId use taskId?
            // Let's check `handleAbandon` original code target.
            // It used `/tasks/employee/${task.id}/abandon`.
            // The route in taskRoutes.js is `router.post("/employee/:assignmentId/abandon", ...)`!
            // WAIT. `task.id` might be TASK ID, not ASSIGNMENT ID.
            // In `getEmployeeTasks`, we usually get the assignment info.
            // Let's verify if `task.id` is assignment id or task id.
            // In `EmployeeTaskDetailPage`, `useParams` gives `taskId`. 
            // `fetchTask` calls `/tasks/${taskId}` (public/admin?) OR `/tasks/employee/assignment/${assignmentId}`?
            // Let's assume `task` object from `fetchTask` result has the correct ID needed.
            // If the backend route expects `:assignmentId`, we must pass assignment ID.
            // If `task.id` is the assignment ID, we are good.
            // If `task.id` is Task ID, we need `task.assignment_id`.

            // Checking `EmployeeTaskDetailPage.jsx` fetch logic would confirm.
            // Assuming current usage `task.id` was working or intended.
            // BUT Route says `:assignmentId`.
            // I will use `task.assignment_id || task.id` to be safe if available.

            // Also removing navigate() from here as confirmLeave handles it.

            await api.post(
                `/tasks/employee/${task.id}/abandon`, // If this endpoint maps to the route I saw... 
                // Wait, route was `/employee/:assignmentId/abandon` under `taskRoutes`?
                // `router` is mounted at `/api/tasks` usually.
                // So `/api/tasks/employee/:assignmentId/abandon`.
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Task reset successfully');
        } catch (error) {
            throw error;
        }
    };

    // Calculate active time for display
    const getActiveTime = () => {
        let totalMs = parseInt(task.accumulated_duration) || 0;
        if (task.started_at && task.status === 'in_progress') {
            const now = new Date();
            const start = new Date(task.started_at);
            totalMs += (now - start);
        }
        return totalMs;
    };

    // Fetch next task for navigation
    const fetchNextTask = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await api.get('/tasks/employee/my-tasks', {
                headers: { Authorization: `Bearer ${token}` }
            });
            const incompleteTasks = response.data.tasks.filter(t =>
                t.id !== parseInt(taskId) &&
                !['completed', 'pending_review'].includes(t.status)
            );
            if (incompleteTasks.length > 0) {
                setNextTask(incompleteTasks[0]);
            }
        } catch (error) {
            console.error('Failed to fetch next task:', error);
        }
    };

    // Called by viewers when they want to submit - shows confirmation modal
    // Called by viewers when they want to submit - shows confirmation modal
    const onCompleteTask = async (data, score) => {
        // Validate Proof if required
        if (task.requires_proof) {
            if ((!proofFiles || proofFiles.length === 0) && !proofUrl) {
                toast.error('Please provide proof of completion (upload file or provided link)');
                return;
            }
        }

        // Validate Signature if required
        if (task.requires_signature) {
            if (!signatureData || signatureData.trim() === '') {
                toast.error('Please sign to acknowledge completion');
                return;
            }
        }

        // Create summary items based on task type
        const summaryItems = [];
        if (task.task_type === 'quiz') {
            try {
                const parsed = typeof data === 'string' ? JSON.parse(data) : data;
                const answeredCount = parsed.answers ? Object.keys(parsed.answers).length : 0;
                summaryItems.push(`${answeredCount} quiz answers`);
                if (score !== null) summaryItems.push(`Score: ${score}%`);
            } catch (e) {
                summaryItems.push('Quiz answers');
            }
        } else if (task.task_type === 'checklist') {
            try {
                const parsed = typeof data === 'string' ? JSON.parse(data) : data;
                summaryItems.push(`${parsed.completedItems?.length || 0} checklist items completed`);
            } catch (e) {
                summaryItems.push('Checklist items');
            }
        } else if (task.task_type === 'training') {
            summaryItems.push('Training completed');
        } else {
            summaryItems.push('Task completed');
        }

        // Store pending submission and show confirmation
        setPendingSubmission({
            data,
            score,
            proof_files: proofFiles,
            proof_url: proofUrl,
            signature_data: signatureData
        });
        setValidationState({ isValid: true, summary: summaryItems });
        setShowSubmitConfirm(true);
    };

    const handleCancelSubmission = () => {
        setShowSubmitConfirm(false);
        setLastSubmissionCancelled(Date.now());
    };

    // Actually submit to backend after confirmation
    const handleConfirmedSubmission = async () => {
        if (!pendingSubmission) return;

        setIsSubmitting(true);
        try {
            const token = localStorage.getItem('token');
            const { data, score, proof_files, proof_url, signature_data } = pendingSubmission;

            // Generate signature audit trail if signature is provided
            let signature_audit = null;
            if (signature_data && task.requires_signature) {
                try {
                    signature_audit = await generateSignatureAuditTrail({
                        name: user?.first_name && user?.last_name
                            ? `${user.first_name} ${user.last_name}`
                            : user?.name || 'Unknown',
                        email: user?.email || 'Unknown',
                        id: user?.id
                    });
                } catch (auditError) {
                    console.error('Failed to capture signature audit:', auditError);
                    // Continue with submission even if audit fails
                    signature_audit = {
                        signed_at: new Date().toISOString(),
                        error: 'Audit capture failed'
                    };
                }
            }

            await api.post(
                `/tasks/employee/${task.id}/complete`,
                {
                    response_data: data,
                    score,
                    proof_files,
                    proof_url,
                    signature_data,
                    signature_audit
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            // Update local task state immediately to reflect completion
            setTask(prev => ({ ...prev, status: 'completed' }));

            setShowSubmitConfirm(false);

            // Fetch next task for navigation
            await fetchNextTask();

            // Determine if this is a quiz that passed/failed
            const passingScore = task.passing_score || 70;

            if (task.task_type === 'quiz' && score !== null && task.quiz_grading_type !== 'manual') {
                if (score >= passingScore) {
                    // Show success modal
                    setCompletionResult({ score, passingScore, missedTopics: [] });
                    setShowSuccessModal(true);
                } else {
                    // Show failure modal
                    // Try to extract missed topics from quiz content
                    let missedTopics = [];
                    try {
                        const quiz = JSON.parse(task.content);
                        const response = typeof data === 'string' ? JSON.parse(data) : data;
                        missedTopics = quiz.questions
                            .filter((q, i) => response.answers?.[i] !== q.correctAnswer)
                            .slice(0, 3)
                            .map(q => q.question.substring(0, 50) + (q.question.length > 50 ? '...' : ''));
                    } catch (e) { }

                    setCompletionResult({
                        score,
                        passingScore,
                        missedTopics,
                        allowRetakes: task.allow_retakes || false
                    });
                    setShowFailureModal(true);
                }
            } else {
                // Non-quiz or manual grading - show success modal
                setCompletionResult({ score, passingScore: null, missedTopics: [] });
                setShowSuccessModal(true);
            }
        } catch (error) {
            console.error('Completion error', error);
            toast.error('Failed to submit');
            setShowSubmitConfirm(false);
        } finally {
            setIsSubmitting(false);
            setPendingSubmission(null);
        }
    };



    // Handle retake quiz (immediate)
    const handleRetakeQuiz = async () => {
        try {
            const token = localStorage.getItem('token');
            await api.post(
                `/tasks/employee/${task.id}/abandon`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setShowFailureModal(false);
            // Refresh to restart
            fetchTask();
            toast.success('Ready for retake!');
        } catch (error) {
            toast.error('Failed to reset for retake');
        }
    };

    // Handle request retake (admin approval)
    const handleRequestRetake = async () => {
        try {
            const token = localStorage.getItem('token');
            await api.post(
                `/tasks/employee/${task.id}/request-retake`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Retake request sent to admin');
            fetchTask(); // Refresh to update status if needed
        } catch (error) {
            console.error('Request retake error', error);
            toast.error('Failed to send retake request');
        }
    };

    // Handle PDF export for completed tasks
    const handleExportPDF = async () => {
        setIsExportingPDF(true);
        try {
            const token = localStorage.getItem('token');
            const assignmentId = task.id || task.assignment_id;

            const response = await api.get(`/tasks/assignment/${assignmentId}/export-pdf`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
            });

            // Create download link
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            // Extract filename from Content-Disposition header or generate one
            const contentDisposition = response.headers['content-disposition'];
            let filename = 'task-document.pdf';
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
                if (filenameMatch) {
                    filename = filenameMatch[1];
                }
            } else {
                const taskTitle = (task.title || 'Task').replace(/[^a-zA-Z0-9]/g, '_').substring(0, 30);
                const employeeName = user?.first_name && user?.last_name
                    ? `${user.first_name}_${user.last_name}`
                    : 'Employee';
                const date = new Date().toISOString().split('T')[0];
                filename = `${taskTitle}_${employeeName}_${date}.pdf`;
            }

            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast.success(`Downloaded: ${filename}`);
        } catch (error) {
            console.error('PDF export error:', error);
            toast.error('Failed to export PDF');
        } finally {
            setIsExportingPDF(false);
        }
    };

    if (loading || !task) {
        return (
            <div className="h-full flex items-center justify-center bg-gray-50 min-h-[500px]">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
        );
    }



    // Helper to render Proof and Signature sections
    const renderProofAndSignature = () => {
        if (!task.requires_proof && !task.requires_signature) return null;

        return (
            <div className="space-y-8 mt-8">
                {/* Proof Upload / Display Section */}
                {task.requires_proof && (
                    <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
                        {['completed', 'pending_review', 'retake_requested'].includes(task.status) ? (
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Submitted Proof</h3>
                                {task.proof_files && (
                                    <div className="space-y-3 mb-4">
                                        <p className="text-sm font-medium text-gray-700">Files:</p>
                                        <div className="grid gap-2">
                                            {(typeof task.proof_files === 'string' ? JSON.parse(task.proof_files) : task.proof_files).map((file, index) => (
                                                <button
                                                    key={index}
                                                    onClick={() => setPreviewFile(file)}
                                                    className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 hover:border-gray-300 transition-colors w-full text-left group"
                                                >
                                                    <div className="p-2 bg-white rounded border border-gray-200 text-gray-500">
                                                        <FileText className="w-5 h-5" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-medium text-gray-900 truncate">{file.file_name}</p>
                                                        <p className="text-xs text-gray-500">Click to preview</p>
                                                    </div>
                                                    <Eye className="w-4 h-4 text-gray-400 group-hover:text-primary" />
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {task.proof_url && (
                                    <div>
                                        <p className="text-sm font-medium text-gray-700 mb-1">Link:</p>
                                        <a
                                            href={task.proof_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-primary hover:underline flex items-center gap-1"
                                        >
                                            {task.proof_url}
                                            <Eye className="w-3 h-3" />
                                        </a>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <ProofFileUpload
                                proofFiles={proofFiles}
                                onProofFilesChange={setProofFiles}
                                proofUrl={proofUrl}
                                onProofUrlChange={setProofUrl}
                                isSubmitting={isSubmitting}
                            />
                        )}
                    </div>
                )}

                {/* Signature Section */}
                {task.requires_signature && (
                    <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
                        {['completed', 'pending_review', 'retake_requested'].includes(task.status) ? (
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Signature</h3>
                                {task.signature_data && (
                                    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 inline-block">
                                        {(task.signature_data.startsWith('http') || task.signature_data.startsWith('data:')) ? (
                                            <button
                                                onClick={() => setPreviewFile({
                                                    file_name: 'Signature',
                                                    file_type: 'image/png',
                                                    file_url: task.signature_data
                                                })}
                                                className="group relative block"
                                                title="Click to view full screen"
                                            >
                                                <img
                                                    src={task.signature_data}
                                                    alt="Signature"
                                                    className="max-h-32 max-w-full object-contain mix-blend-multiply group-hover:opacity-90 transition-opacity"
                                                />
                                                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/5 rounded-lg">
                                                    <Eye className="w-6 h-6 text-gray-700" />
                                                </div>
                                            </button>
                                        ) : (
                                            <p className="text-xl font-signature text-gray-800 px-4 py-2">
                                                {task.signature_data}
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <SignatureSection
                                signatureData={signatureData}
                                onSignatureChange={setSignatureData}
                                isSubmitting={isSubmitting}
                            />
                        )}
                    </div>
                )}
            </div>
        );
    };

    // If active workspace, use full screen z-index overlay
    const containerClasses = isWorkspaceMode
        ? "fixed inset-0 z-[100] bg-gray-50 flex flex-col h-screen w-screen overflow-hidden"
        : "flex flex-col h-full min-h-screen bg-gray-50";

    return (
        <div className={containerClasses}>
            {/* Blocking Overlay for Paused State */}
            {isPaused && (
                <PausedOverlay
                    task={task}
                    onResume={handleResume}
                    onAbandon={handleAbandon}
                    accumulatedDuration={task.accumulated_duration}
                />
            )}

            {/* Pause Confirmation Modal */}
            {showPauseConfirm && (
                <div className="fixed inset-0 z-[150] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in">
                    <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full text-center">
                        <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <PauseCircle className="w-6 h-6 text-amber-600" />
                        </div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">Pause Task?</h3>
                        <p className="text-gray-500 mb-6">Your progress will be saved. You can resume this task at any time from your dashboard.</p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowPauseConfirm(false)}
                                className="flex-1 py-2 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    setShowPauseConfirm(false);
                                    handlePause('manual');
                                }}
                                className="flex-1 py-2 px-4 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-medium transition-colors"
                            >
                                Pause Task
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Submission Confirmation Modal */}
            <SubmissionConfirmModal
                isOpen={showSubmitConfirm}
                onClose={() => {
                    setShowSubmitConfirm(false);
                    setPendingSubmission(null);
                }}
                onConfirm={handleConfirmedSubmission}
                task={task}
                activeTime={getActiveTime()}
                validationSummary={{ items: validationState.summary }}
                isSubmitting={isSubmitting}
            />

            {/* Success Modal */}
            <TaskSuccessModal
                isOpen={showSuccessModal}
                onClose={() => {
                    setShowSuccessModal(false);
                    navigate(`${basePath}/tasks`);
                }}
                task={task}
                score={completionResult?.score}
                passingScore={completionResult?.passingScore}
                nextTask={nextTask}
                onViewNextTask={() => {
                    setShowSuccessModal(false);
                    if (nextTask) navigate(`${basePath}/tasks/${nextTask.id}`);
                }}
                onBackToDashboard={() => {
                    setShowSuccessModal(false);
                    navigate(`${basePath}/tasks`);
                }}
            />

            {/* Failure Modal */}
            <TaskFailureModal
                isOpen={showFailureModal}
                onClose={() => {
                    setShowFailureModal(false);
                    navigate(`${basePath}/tasks`);
                }}
                task={task}
                score={completionResult?.score}
                passingScore={completionResult?.passingScore}
                missedTopics={completionResult?.missedTopics || []}
                allowRetakes={completionResult?.allowRetakes}
                onRetake={handleRetakeQuiz}
                onBackToDashboard={() => {
                    setShowFailureModal(false);
                    navigate(`${basePath}/tasks`);
                }}
            />

            {/* Leave Confirmation Modal - Placed with other modals */}
            {showLeaveConfirm && (
                <div className="fixed inset-0 z-[160] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in">
                    <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full">
                        <div className="bg-amber-50 rounded-full w-12 h-12 flex items-center justify-center mb-4 text-amber-600 mx-auto">
                            <LogOut className="w-6 h-6" />
                        </div>
                        <h3 className="text-xl font-bold text-center text-gray-900 mb-2">Leave without saving?</h3>
                        <p className="text-center text-gray-500 mb-6">
                            Your unsaved progress will be lost. Use <strong>Pause</strong> if you want to save your work.
                        </p>

                        <div className="flex gap-3">
                            <button
                                onClick={cancelLeave}
                                className="flex-1 py-2.5 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmLeave}
                                className="flex-1 py-2.5 px-4 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors shadow-sm"
                            >
                                Leave Task
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* HEADER - Fixed */}
            <header className="h-16 bg-white border-b border-gray-200 shadow-sm flex items-center justify-between px-6 z-10 shrink-0">
                <div className="flex items-center gap-4">
                    {!isWorkspaceMode && (
                        <button
                            onClick={() => navigate(`${basePath}/tasks`, { state: { activeTab: (task?.status === 'completed' || task?.status === 'pending_review') ? 'done' : 'todo' } })}
                            className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 transition-colors"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </button>
                    )}
                    <div>
                        <h1 className="text-lg font-bold text-gray-900 truncate max-w-xl">{task.title}</h1>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                            <span className="uppercase tracking-wider font-semibold">{task.task_type}</span>
                            {task.due_date && (
                                <>
                                    <span>•</span>
                                    <span>Due {new Date(task.due_date).toLocaleDateString()}</span>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-4">
                    {/* Pause Button / Leave Button - Only show if interacting */}
                    {isWorkspaceMode && !isPaused && (
                        <>
                            <button
                                onClick={() => setShowLeaveConfirm(true)}
                                className="px-4 py-2 hover:bg-gray-100 text-gray-700 rounded-lg font-medium transition-colors border border-transparent hover:border-gray-200"
                            >
                                Leave
                            </button>
                            <button
                                onClick={() => setShowPauseConfirm(true)}
                                className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors border border-gray-200"
                            >
                                <PauseCircle className="w-5 h-5" />
                                <span>Pause</span>
                            </button>
                        </>
                    )}

                    {/* Exit/Back Button (when not interactive) */}
                    {!isWorkspaceMode && (
                        <button
                            onClick={() => navigate('/employee/tasks', { state: { activeTab: (task?.status === 'completed' || task?.status === 'pending_review') ? 'done' : 'todo' } })}
                            className="text-sm font-medium text-gray-500 hover:text-gray-900"
                        >
                            Close
                        </button>
                    )}
                </div>
            </header>

            {/* MAIN CONTENT - Scrollable */}
            <main className="flex-1 overflow-y-auto w-full">
                <div className="max-w-5xl mx-auto p-8 pb-32">
                    {/* Status Banners (for Not Started / Completed) */}
                    {!isWorkspaceMode && (
                        <div className="mb-8">
                            {task.status === 'completed' ? (
                                <div className="bg-green-50 border border-green-100 rounded-xl p-6 flex items-center justify-between text-green-800">
                                    <div className="flex items-center gap-4">
                                        <CheckCircle className="w-8 h-8 text-green-600" />
                                        <div>
                                            <h3 className="font-bold text-lg">Task Completed</h3>
                                            <p>Great job! This task has been submitted.</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={handleExportPDF}
                                        disabled={isExportingPDF}
                                        className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 transition-colors shadow-sm flex items-center gap-2"
                                    >
                                        {isExportingPDF ? (
                                            <>
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                <span>Exporting...</span>
                                            </>
                                        ) : (
                                            <>
                                                <Download className="w-4 h-4" />
                                                <span>Export Document</span>
                                            </>
                                        )}
                                    </button>
                                </div>
                            ) : task.status === 'pending_review' ? (
                                <div className="bg-amber-50 border border-amber-100 rounded-xl p-6 flex items-center justify-between text-amber-800">
                                    <div className="flex items-center gap-4">
                                        <Clock className="w-8 h-8 text-amber-600" />
                                        <div>
                                            <h3 className="font-bold text-lg">Pending Review</h3>
                                            <p>Your submission is awaiting administrator review.</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={handleExportPDF}
                                        disabled={isExportingPDF}
                                        className="px-4 py-2 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 disabled:opacity-50 transition-colors shadow-sm flex items-center gap-2"
                                    >
                                        {isExportingPDF ? (
                                            <>
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                <span>Exporting...</span>
                                            </>
                                        ) : (
                                            <>
                                                <Download className="w-4 h-4" />
                                                <span>Export Document</span>
                                            </>
                                        )}
                                    </button>
                                </div>
                            ) : task.status === 'retake_requested' ? (
                                <div className="bg-orange-50 border border-orange-100 rounded-xl p-6 flex items-center gap-4 text-orange-800">
                                    <Clock className="w-8 h-8 text-orange-600" />
                                    <div>
                                        <h3 className="font-bold text-lg">Retake Requested</h3>
                                        <p>Your request to retake this quiz is pending administrator approval.</p>
                                    </div>
                                </div>
                            ) : task.status === 'pending' || task.status === 'not_started' || task.status === 'viewed' || task.status === 'overdue' || task.status === 'missed' ? (
                                <div className="bg-white border-2 border-dashed border-gray-300 rounded-xl p-12 text-center">
                                    <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                                        <PlayCircle className="w-10 h-10 text-primary" />
                                    </div>
                                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Ready to start?</h2>
                                    <p className="text-gray-500 mb-8 max-w-md mx-auto">This task is ready for you. Click the button below to start the timer and begin your work.</p>
                                    <button
                                        onClick={handleStartTask}
                                        className="inline-flex items-center gap-2 px-8 py-4 bg-primary hover:bg-primary-hover text-white rounded-xl font-bold text-lg shadow-xl shadow-primary/20 transition-all hover:scale-105"
                                    >
                                        <PlayCircle className="w-6 h-6" />
                                        Start Task Now
                                    </button>
                                </div>
                            ) : null}
                        </div>
                    )}

                    {/* Actual Content Viewer */}
                    {/* Preview Mode Badge - Show when not in workspace mode and task not completed */}
                    {!isWorkspaceMode && task.status !== 'completed' && (
                        <div className="mb-4 flex items-center gap-2">
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-xs font-semibold">
                                <AlertCircle className="w-3 h-3" />
                                Preview Mode
                            </span>
                            <span className="text-xs text-gray-500">Content is read-only until you start the task</span>
                        </div>
                    )}
                    <div className={`bg-white shadow-sm rounded-xl p-8 min-h-[400px] ${!isWorkspaceMode && task.task_type !== 'quiz' && task.task_type !== 'combined' && !task.is_combined ? 'opacity-80 pointer-events-none grayscale-[0.3]' : ''}`}>
                        {/* Task Description & Attachments - Always visible */}
                        {(true) && (
                            <div className="mb-8 pb-8 border-b border-gray-100">
                                <h2 className="text-xl font-bold text-gray-900 mb-4">{task.title}</h2>
                                {task.description && (
                                    <div className="text-gray-600 mb-6 whitespace-pre-wrap">{task.description}</div>
                                )}

                                {task.attachments && task.attachments.length > 0 && (
                                    <div className="bg-gray-50 rounded-lg p-4">
                                        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                            <Save className="w-4 h-4" />
                                            Attached Files
                                        </h3>
                                        <div className="grid gap-2">
                                            {/* Handle both stringified and parsed attachments */}
                                            {(typeof task.attachments === 'string' ? JSON.parse(task.attachments) : task.attachments).map((file, index) => (
                                                <a
                                                    key={index}
                                                    href={file.file_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-md hover:border-primary hover:text-primary transition-colors group"
                                                >
                                                    <div className="p-2 bg-gray-100 rounded group-hover:bg-primary/10">
                                                        <Save className="w-4 h-4 text-gray-500 group-hover:text-primary" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-medium truncate">{file.file_name}</p>
                                                        <p className="text-xs text-gray-500">{(file.file_size / 1024).toFixed(1)} KB</p>
                                                    </div>
                                                </a>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Combined Task Viewer */}
                        {(task.task_type === 'combined' || task.is_combined) && (
                            <CombinedTaskViewer
                                task={task}
                                assignment={task}
                                employeeInfo={user}
                                onSectionComplete={(sectionId, data) => {
                                    // Refresh task to get updated progress
                                    fetchTask();
                                }}
                                onAllComplete={(completionResults) => {
                                    // Handle combined task completion with detailed results
                                    if (completionResults) {
                                        const score = completionResults.aggregated_score;
                                        const passingScore = completionResults.passing_score || 70;
                                        const passed = completionResults.passed;
                                        const requiresReview = completionResults.requires_review;

                                        setCompletionResult({
                                            score,
                                            passingScore,
                                            missedTopics: [],
                                            sectionResults: completionResults.section_results,
                                            requiresReview
                                        });

                                        // Refresh task to get the updated status
                                        fetchTask();

                                        // Fetch next task for navigation
                                        fetchNextTask();

                                        // Show appropriate modal
                                        if (requiresReview) {
                                            // Task submitted for review - show success modal with review message
                                            setShowSuccessModal(true);
                                        } else if (score !== null && score < passingScore) {
                                            // Failed quiz sections
                                            setShowFailureModal(true);
                                        } else {
                                            // Success!
                                            setShowSuccessModal(true);
                                        }
                                    } else {
                                        // Fallback for old behavior
                                        setCompletionResult({ score: 100, passingScore: 0 });
                                        fetchTask();
                                        fetchNextTask();
                                        setShowSuccessModal(true);
                                    }
                                }}
                                previewMode={!isWorkspaceMode && task.status !== 'completed' && task.status !== 'pending_review'} // Enable preview mode when not started AND not in a post-start/review state
                            />
                        )}
                        {task.task_type === 'quiz' && !task.is_combined && (
                            isWorkspaceMode || task.status === 'completed' ? (
                                <QuizViewer
                                    content={task.content}
                                    onComplete={onCompleteTask}
                                    taskStatus={task.status}
                                    previousResponse={task.response_data}
                                    lastSubmissionCancelled={lastSubmissionCancelled}
                                    allowRetakes={true}
                                    onRequestRetake={handleRequestRetake}
                                >
                                    {renderProofAndSignature()}
                                </QuizViewer>
                            ) : (
                                <div className="flex flex-col items-center justify-center py-12 text-center text-gray-500 border-2 border-dashed border-gray-200 rounded-xl bg-gray-50/50">
                                    <div className="bg-white p-4 rounded-full shadow-sm mb-6">
                                        <AlertCircle className="w-10 h-10 text-gray-400" />
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900 mb-2">Quiz Content Hidden</h3>
                                    <p className="max-w-md mx-auto text-gray-600 mb-8">
                                        To maintain integrity, the questions for this quiz are hidden until you officially start the task.
                                    </p>

                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-2xl w-full px-4">
                                        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex flex-col items-center">
                                            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Time Estimate</span>
                                            <span className="text-lg font-bold text-gray-900">{task.estimated_duration ? `${task.estimated_duration} mins` : 'Self-paced'}</span>
                                        </div>
                                        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex flex-col items-center">
                                            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Passing Score</span>
                                            <span className="text-lg font-bold text-gray-900">{task.passing_score || 80}%</span>
                                        </div>
                                        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex flex-col items-center">
                                            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Questions</span>
                                            <span className="text-lg font-bold text-gray-900">
                                                {(() => {
                                                    try {
                                                        const parsed = typeof task.content === 'string' ? JSON.parse(task.content) : task.content;
                                                        return parsed.questions?.length || 'Unknown';
                                                    } catch (e) {
                                                        return 'Unknown';
                                                    }
                                                })()}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )
                        )}
                        {task.task_type === 'training' && !task.is_combined && (
                            <TrainingViewer
                                content={task.content}
                                onComplete={onCompleteTask}
                                lastSubmissionCancelled={lastSubmissionCancelled}
                                previousResponse={task.response_data}
                            >
                                {renderProofAndSignature()}
                            </TrainingViewer>
                        )}
                        {task.task_type === 'checklist' && !task.is_combined && (
                            <ChecklistViewer
                                content={task.content}
                                onComplete={onCompleteTask}
                                previousResponse={task.response_data}
                                lastSubmissionCancelled={lastSubmissionCancelled}
                            >
                                {renderProofAndSignature()}
                            </ChecklistViewer>
                        )}
                        {task.task_type === 'task' && !task.is_combined && (
                            isWorkspaceMode || task.status === 'completed' ? (
                                <div className="space-y-8">
                                    <div className="prose max-w-none">
                                        <div className="whitespace-pre-wrap text-gray-700 leading-relaxed">{task.content}</div>
                                    </div>

                                    {renderProofAndSignature()}
                                </div>
                            ) : (
                                <div className="flex flex-col items-center justify-center py-12 text-center text-gray-500 border-2 border-dashed border-gray-200 rounded-xl bg-gray-50/50">
                                    <div className="bg-white p-4 rounded-full shadow-sm mb-6">
                                        <FileText className="w-10 h-10 text-gray-400" />
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900 mb-2">Task Content Hidden</h3>
                                    <p className="max-w-md mx-auto text-gray-600 mb-8">
                                        The details for this task are hidden until you officially start the task.
                                    </p>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-xl w-full px-4">
                                        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex flex-col items-center">
                                            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Time Estimate</span>
                                            <span className="text-lg font-bold text-gray-900">{task.estimated_duration ? `${task.estimated_duration} mins` : 'Self-paced'}</span>
                                        </div>
                                        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex flex-col items-center">
                                            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Due Date</span>
                                            <span className="text-lg font-bold text-gray-900">
                                                {task.due_date ? new Date(task.due_date).toLocaleDateString() : 'No Due Date'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )
                        )}
                    </div>
                </div>
            </main >

            {/* FOOTER - Fixed (Only in Workspace Mode) */}
            {
                isWorkspaceMode && (
                    <footer className="h-20 bg-white border-t border-gray-200 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] flex items-center justify-between px-8 shrink-0 z-20">
                        <div className="text-sm text-gray-500">
                            {/* Status / Auto-save indicator could go here */}
                            <span className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                                Runnning
                            </span>
                        </div>

                        <div className="flex items-center gap-4">
                            {/* Submit Button logic varies by type, but for 'task' type it's explicit. 
                             For others, the Viewer usually handles it, but we can have a global submit if needed.
                             Wait! Quiz/Checklist Viewers have their own internal submit buttons. 
                             We should probably hide this footer for them OR allow the footer to trigger the internal submit.
                             For now, let's keep the footering simple. 
                             If 'task' type, show Submit button here. 
                             If others, maybe just show a "Finish in the section above" or similar?
                              एक्चुअली usually 'Submit' is at the bottom of the content. 
                             The PROMPT said "Fixed footer with a 'Submit' button". 
                             I will add a global Submit button that triggers the action.
                         */}

                            {task.task_type === 'task' ? (
                                <button
                                    onClick={() => onCompleteTask({}, null)}
                                    disabled={isSubmitting}
                                    className="px-8 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-bold shadow-lg shadow-green-200 transition-all disabled:bg-gray-300 disabled:shadow-none flex items-center gap-2"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                            Submitting...
                                        </>
                                    ) : (
                                        <>
                                            <CheckCircle className="w-5 h-5" />
                                            Submit Task
                                        </>
                                    )}
                                </button>
                            ) : (
                                <div className="text-gray-400 italic text-sm">
                                    Complete all items above to submit
                                </div>
                            )}
                        </div>
                    </footer>
                )
            }

            {/* Modals */}
            <SubmissionConfirmModal
                isOpen={showSubmitConfirm}
                onClose={handleCancelSubmission}
                onConfirm={handleConfirmedSubmission}
                task={task}
                activeTime={activeTime}
                validationSummary={validationState}
                isSubmitting={isSubmitting}
            />
            {/* ... other modals ... */}
            <TaskSuccessModal
                isOpen={showSuccessModal}
                onClose={() => {
                    // Just close modal to let user review the results on the page
                    // We don't navigate away, allowing them to see the detailed breakdown
                    setShowSuccessModal(false);
                }}
                task={task}
                score={completionResult?.score}
                passingScore={completionResult?.passingScore}
                onNextTask={() => {
                    isLeavingRef.current = true;
                    setShowSuccessModal(false);
                    if (nextTask) {
                        navigate(`/employee/tasks/${nextTask.id}`);
                    } else {
                        navigate('/employee/tasks', { state: { activeTab: 'done' } });
                    }
                }}
                nextTaskAvailable={!!nextTask}
                onViewNextTask={() => {
                    isLeavingRef.current = true;
                    setShowSuccessModal(false);
                    if (nextTask) {
                        navigate(`/employee/tasks/${nextTask.id}`);
                    }
                }}
                onBackToDashboard={() => {
                    isLeavingRef.current = true;
                    setShowSuccessModal(false);
                    navigate('/employee/tasks', { state: { activeTab: 'done' } });
                }}
            />

            <TaskFailureModal
                isOpen={showFailureModal}
                onClose={() => {
                    // Just close modal to let user review the results on the page
                    setShowFailureModal(false);
                }}
                task={task}
                score={completionResult?.score}
                passingScore={completionResult?.passingScore}
                missedTopics={completionResult?.missedTopics}
                allowRetakes={completionResult?.allowRetakes}
                onRetake={async () => {
                    await handleRetakeQuiz(); // This navigates or resets
                    setShowFailureModal(false);
                }}
                onReviewMaterial={() => {
                    setShowFailureModal(false);
                    // Stay on page to review content
                }}
                onBackToDashboard={() => {
                    isLeavingRef.current = true;
                    setShowFailureModal(false);
                    navigate('/employee/tasks', { state: { activeTab: 'todo' } }); // Failure usually means it's still todo/retake
                }}
            />

            <FileViewer
                file={previewFile}
                isOpen={!!previewFile}
                onClose={() => setPreviewFile(null)}
            />

        </div >
    );
};

export default EmployeeTaskDetailPage;
